import { useEffect, useState, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { fmtMoney, fmtNumber, fmtDate } from "@/lib/format";
import { DownloadSimple } from "@phosphor-icons/react";

function downloadCsv(filename, rows) {
  if (!rows.length) return;
  const cols = Object.keys(rows[0]);
  const escape = (v) => {
    const s = v === null || v === undefined ? "" : String(v);
    if (/[",\n]/.test(s)) return `"${s.replaceAll('"', '""')}"`;
    return s;
  };
  const lines = [cols.join(",")].concat(rows.map((r) => cols.map((c) => escape(r[c])).join(",")));
  const blob = new Blob([lines.join("\n")], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

function Tile({ label, value, accent, sub }) {
  const cls = accent === "danger" ? "border-destructive bg-destructive/5 text-destructive"
    : accent === "good" ? "border-[#00B341] bg-[#00B341]/5 text-[#00713a]"
    : accent === "warn" ? "border-amber-500 bg-amber-50 text-amber-700"
    : "border-canvas-line";
  return (
    <div className={`border-2 ${cls} p-4`}>
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">{label}</div>
      <div className="font-display text-2xl font-bold tabular-nums">{value}</div>
      {sub && <div className="font-mono text-[10px] text-ink-muted mt-0.5">{sub}</div>}
    </div>
  );
}

function ReportShell({ children, csvRows, csvName }) {
  return (
    <div className="space-y-4">
      {children}
      {csvRows && csvRows.length > 0 && (
        <div className="flex justify-end">
          <button onClick={() => downloadCsv(csvName, csvRows)}
            className="inline-flex items-center gap-1 px-3 py-1.5 border border-ink text-xs font-mono uppercase tracking-wider hover:bg-ink hover:text-white">
            <DownloadSimple size={11} weight="bold" /> Export CSV
          </button>
        </div>
      )}
    </div>
  );
}

export function InventoryAgingReport({ modelCategory, branchId, branchTypeId }) {
  const [d, setD] = useState({ rows: [], buckets: {}, total_count: 0, total_cost: 0 });
  useEffect(() => { (async () => {
    try {
      const params = {};
      if (modelCategory) params.model_category = modelCategory;
      if (branchId) params.branch_id = branchId;
      if (branchTypeId) params.branch_type_id = branchTypeId;
      const { data } = await api.get("/reports/inventory-aging", { params });
      setD(data);
    } catch (e) { toast.error(formatApiError(e)); }
  })(); }, [modelCategory, branchId, branchTypeId]);
  const csv = useMemo(() => d.rows.map(r => ({
    VIN: r.vin, Package: r.package, Model: r.model_category, Color: r.color, Branch: r.branch_name,
    Status: r.status, AgeDays: r.age_days, PurchaseCost: r.purchase_cost, Price: r.price,
  })), [d.rows]);
  const b = d.buckets || {};
  return <ReportShell csvRows={csv} csvName="inventory-aging.csv">
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
      <Tile label="0–30 days" value={fmtNumber(b["0_30"] || 0)} accent="good"/>
      <Tile label="31–60 days" value={fmtNumber(b["31_60"] || 0)}/>
      <Tile label="61–90 days" value={fmtNumber(b["61_90"] || 0)} accent="warn"/>
      <Tile label="91–180 days" value={fmtNumber(b["91_180"] || 0)} accent="warn"/>
      <Tile label="180+ days" value={fmtNumber(b["180_plus"] || 0)} accent="danger"/>
    </div>
    <div className="text-xs text-ink-muted">
      Total in stock: <b>{fmtNumber(d.total_count)}</b> · Capital locked: <b>{fmtMoney(d.total_cost)}</b>
    </div>
    <div className="border-2 border-ink overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="bg-canvas-panel"><tr>
          {["VIN","Package","Model","Color","Branch","Status","Age","Cost","Price"].map(h =>
            <th key={h} className="text-left px-2 py-2 font-mono text-[10px] uppercase whitespace-nowrap">{h}</th>)}
        </tr></thead>
        <tbody>{d.rows.slice(0, 200).map(r => (
          <tr key={r.vin} className="border-t border-canvas-line">
            <td className="px-2 py-1.5 font-mono text-[11px]">{r.vin}</td>
            <td className="px-2 py-1.5">{r.package}</td>
            <td className="px-2 py-1.5 font-mono text-[10px] uppercase tracking-wider text-ink-muted">{r.model_category || "—"}</td>
            <td className="px-2 py-1.5 text-ink-muted">{r.color}</td>
            <td className="px-2 py-1.5">{r.branch_name}</td>
            <td className="px-2 py-1.5 font-mono text-[10px] uppercase">{r.status}</td>
            <td className={`px-2 py-1.5 font-mono tabular-nums ${r.age_days > 90 ? "text-destructive font-bold" : ""}`}>{r.age_days}d</td>
            <td className="px-2 py-1.5 text-right font-mono tabular-nums">{fmtMoney(r.purchase_cost)}</td>
            <td className="px-2 py-1.5 text-right font-mono tabular-nums">{fmtMoney(r.price)}</td>
          </tr>))}</tbody>
      </table>
      {d.rows.length > 200 && <div className="p-2 text-xs text-ink-muted">Showing first 200 of {d.rows.length}. Export CSV for full list.</div>}
    </div>
  </ReportShell>;
}

export function CashBookReport({ dateFrom, dateTo, branchId, branchTypeId }) {
  const [d, setD] = useState({ daily: [], totals: {}, receipts_by_method: {}, payouts_by_category: {}, payouts_by_method: {} });
  useEffect(() => { (async () => {
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (branchId) params.branch_id = branchId;
      if (branchTypeId) params.branch_type_id = branchTypeId;
      const { data } = await api.get("/reports/cash-book", { params });
      setD(data);
    } catch (e) { toast.error(formatApiError(e)); }
  })(); }, [dateFrom, dateTo, branchId, branchTypeId]);
  const t = d.totals || {};
  const csv = useMemo(() => d.daily.map(r => ({ Date: r.date, Receipts: r.receipts, Payouts: r.payouts, Net: r.net, Running: r.running })), [d.daily]);
  return <ReportShell csvRows={csv} csvName="cash-book.csv">
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
      <Tile label="Total Receipts" value={fmtMoney(t.receipts)} accent="good"/>
      <Tile label="Total Payouts" value={fmtMoney(t.payouts)} accent="danger"/>
      <Tile label="Net" value={fmtMoney(t.net)} accent={t.net >= 0 ? "good" : "danger"}/>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <div className="border border-canvas-line p-3">
        <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-1">Receipts by Method</div>
        {Object.keys(d.receipts_by_method).length === 0 && <div className="text-xs text-ink-muted">—</div>}
        {Object.entries(d.receipts_by_method).map(([k,v]) =>
          <div key={k} className="flex justify-between text-xs"><span className="capitalize">{k}</span><span className="font-mono tabular-nums">{fmtMoney(v)}</span></div>)}
      </div>
      <div className="border border-canvas-line p-3">
        <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-1">Payouts by Method</div>
        {Object.keys(d.payouts_by_method || {}).length === 0 && <div className="text-xs text-ink-muted">—</div>}
        {Object.entries(d.payouts_by_method || {}).map(([k,v]) =>
          <div key={k} className="flex justify-between text-xs"><span className="capitalize">{k}</span><span className="font-mono tabular-nums">{fmtMoney(v)}</span></div>)}
      </div>
      <div className="border border-canvas-line p-3">
        <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-1">Payouts by Category</div>
        {Object.keys(d.payouts_by_category).length === 0 && <div className="text-xs text-ink-muted">—</div>}
        {Object.entries(d.payouts_by_category).map(([k,v]) =>
          <div key={k} className="flex justify-between text-xs"><span>{k}</span><span className="font-mono tabular-nums">{fmtMoney(v)}</span></div>)}
      </div>
    </div>
    <div className="border-2 border-ink overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="bg-canvas-panel"><tr>
          <th className="px-2 py-2 font-mono text-[10px] uppercase text-left">Date</th>
          {["Receipts","Payouts","Net","Running"].map(h => (
            <th key={h} className="px-2 py-2 font-mono text-[10px] uppercase text-right">{h}</th>
          ))}
        </tr></thead>
        <tbody>{d.daily.length === 0 ? (
          <tr><td colSpan={5} className="px-2 py-10 text-center text-ink-muted text-sm">
            No cash activity in this window.
          </td></tr>
        ) : d.daily.map(r => (
          <tr key={r.date} className="border-t border-canvas-line">
            <td className="px-2 py-1.5 font-mono">{r.date}</td>
            <td className="px-2 py-1.5 text-right font-mono tabular-nums text-[#00713a]">{fmtMoney(r.receipts)}</td>
            <td className="px-2 py-1.5 text-right font-mono tabular-nums text-destructive">{fmtMoney(r.payouts)}</td>
            <td className="px-2 py-1.5 text-right font-mono tabular-nums">{fmtMoney(r.net)}</td>
            <td className="px-2 py-1.5 text-right font-mono tabular-nums font-bold">{fmtMoney(r.running)}</td>
          </tr>))}</tbody>
      </table>
    </div>
  </ReportShell>;
}

export function BookingFunnelReport({ dateFrom, dateTo, modelCategory, branchId, branchTypeId }) {
  const [d, setD] = useState({ totals: {}, by_package: [] });
  useEffect(() => { (async () => {
    try {
      const params = { date_from: dateFrom, date_to: dateTo };
      if (modelCategory) params.model_category = modelCategory;
      if (branchId) params.branch_id = branchId;
      if (branchTypeId) params.branch_type_id = branchTypeId;
      const { data } = await api.get("/reports/booking-funnel", { params });
      setD(data);
    } catch (e) { toast.error(formatApiError(e)); }
  })(); }, [dateFrom, dateTo, modelCategory, branchId, branchTypeId]);
  const t = d.totals || {};
  const rows = d.by_package || [];
  return <ReportShell csvRows={rows} csvName="booking-funnel.csv">
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
      <Tile label="Bookings" value={fmtNumber(t.total || 0)}/>
      <Tile label="Active" value={fmtNumber(t.active || 0)}/>
      <Tile label="Confirmed" value={fmtNumber(t.confirmed || 0)} accent="good"/>
      <Tile label="Converted → Sale" value={fmtNumber(t.converted || 0)} accent="good"/>
      <Tile label="Cancelled" value={fmtNumber(t.cancelled || 0)} accent="danger"/>
    </div>
    <div className="text-sm">Conversion Rate: <b className="text-[#00713a]">{t.conv_rate || 0}%</b></div>
    <div className="border-2 border-ink overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="bg-canvas-panel"><tr>
          <th className="px-2 py-2 font-mono text-[10px] uppercase text-left">Package</th>
          {["Total","Active","Converted","Cancelled","Conv %"].map(h => (
            <th key={h} className="px-2 py-2 font-mono text-[10px] uppercase text-right whitespace-nowrap">{h}</th>
          ))}
        </tr></thead>
        <tbody>{rows.length === 0 ? (
          <tr><td colSpan={6} className="px-2 py-10 text-center text-ink-muted text-sm">
            No bookings in this window.
          </td></tr>
        ) : rows.map(r => (
          <tr key={r.package} className="border-t border-canvas-line">
            <td className="px-2 py-1.5">{r.package}</td>
            <td className="px-2 py-1.5 text-right font-mono tabular-nums">{r.total}</td>
            <td className="px-2 py-1.5 text-right font-mono tabular-nums">{r.active}</td>
            <td className="px-2 py-1.5 text-right font-mono tabular-nums text-[#00713a]">{r.converted}</td>
            <td className="px-2 py-1.5 text-right font-mono tabular-nums text-destructive">{r.cancelled}</td>
            <td className="px-2 py-1.5 text-right font-mono tabular-nums font-bold">{r.conv_rate}%</td>
          </tr>))}</tbody>
      </table>
    </div>
  </ReportShell>;
}

export function SlaBreachesReport({ branchId, branchTypeId, modelCategory }) {
  const [d, setD] = useState({ rows: [], by_stage: {}, total: 0 });
  useEffect(() => { (async () => {
    try {
      const params = {};
      if (branchId) params.branch_id = branchId;
      if (branchTypeId) params.branch_type_id = branchTypeId;
      if (modelCategory) params.model_category = modelCategory;
      const { data } = await api.get("/reports/sla-breaches", { params });
      setD(data);
    }
    catch (e) { toast.error(formatApiError(e)); }
  })(); }, [branchId, branchTypeId, modelCategory]);
  const csv = useMemo(() => d.rows.map(r => ({
    VIN: r.vin, Customer: r.customer_name, Model: r.vehicle_model, Branch: r.branch_name,
    Stage: r.stage_label, SLA: r.sla_days, Age: r.age_days, Overdue: r.days_overdue,
  })), [d.rows]);
  return <ReportShell csvRows={csv} csvName="sla-breaches.csv">
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
      <Tile label="Total Overdue" value={fmtNumber(d.total)} accent={d.total > 0 ? "danger" : "good"}/>
      <div className="border-2 border-canvas-line p-4 sm:col-span-2">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">By Stuck Stage</div>
        <div className="flex flex-wrap gap-3 text-sm">
          {Object.entries(d.by_stage).map(([k,v]) =>
            <span key={k} className="font-mono"><b>{v}</b> stuck on <span className="text-ink-muted capitalize">{k}</span></span>)}
        </div>
      </div>
    </div>
    <div className="border-2 border-ink overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="bg-canvas-panel"><tr>
          {["VIN","Customer","Model","Branch","Stuck On","SLA","Age","Overdue"].map(h => <th key={h} className="px-2 py-2 font-mono text-[10px] uppercase text-left">{h}</th>)}
        </tr></thead>
        <tbody>{d.rows.map(r => (
          <tr key={r.registration_id} className="border-t border-canvas-line">
            <td className="px-2 py-1.5 font-mono text-[11px]">{r.vin}</td>
            <td className="px-2 py-1.5">{r.customer_name}</td>
            <td className="px-2 py-1.5">{r.vehicle_model}</td>
            <td className="px-2 py-1.5 text-ink-muted">{r.branch_name}</td>
            <td className="px-2 py-1.5">{r.stage_label}</td>
            <td className="px-2 py-1.5 text-right font-mono tabular-nums">{r.sla_days}d</td>
            <td className="px-2 py-1.5 text-right font-mono tabular-nums">{r.age_days}d</td>
            <td className="px-2 py-1.5 text-right font-mono tabular-nums font-bold text-destructive">+{r.days_overdue}d</td>
          </tr>))}</tbody>
      </table>
      {d.rows.length === 0 && <div className="p-10 text-center text-ink-muted text-sm">🎉 No SLA breaches — every pipeline is on schedule.</div>}
    </div>
  </ReportShell>;
}

export function SalespersonScoreboard({ dateFrom, dateTo, modelCategory, branchId, branchTypeId }) {
  const [d, setD] = useState({ rows: [], total_units: 0, total_revenue: 0 });
  useEffect(() => { (async () => {
    try {
      const params = { date_from: dateFrom, date_to: dateTo };
      if (modelCategory) params.model_category = modelCategory;
      if (branchId) params.branch_id = branchId;
      if (branchTypeId) params.branch_type_id = branchTypeId;
      const { data } = await api.get("/reports/salesperson-scoreboard", { params });
      setD(data);
    } catch (e) { toast.error(formatApiError(e)); }
  })(); }, [dateFrom, dateTo, modelCategory, branchId, branchTypeId]);
  return <ReportShell csvRows={d.rows} csvName="salesperson-scoreboard.csv">
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      <Tile label="Total Units Sold" value={fmtNumber(d.total_units)}/>
      <Tile label="Total Revenue" value={fmtMoney(d.total_revenue)} accent="good"/>
    </div>
    <div className="border-2 border-ink overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-canvas-panel"><tr>
          <th className="px-2 py-2 font-mono text-[10px] uppercase text-left w-8">#</th>
          <th className="px-2 py-2 font-mono text-[10px] uppercase text-left">Salesperson</th>
          {["Units","Revenue","Avg Ticket","Discount Given","Commission"].map(h => (
            <th key={h} className="px-2 py-2 font-mono text-[10px] uppercase text-right whitespace-nowrap">{h}</th>
          ))}
        </tr></thead>
        <tbody>{d.rows.length === 0 ? (
          <tr><td colSpan={7} className="px-2 py-10 text-center text-ink-muted text-sm">
            No salesperson activity in this window.
          </td></tr>
        ) : d.rows.map((r, i) => (
          <tr key={r.agent_id} className="border-t border-canvas-line">
            <td className="px-2 py-1.5 font-mono">{i + 1}</td>
            <td className="px-2 py-1.5 font-semibold">{r.agent_name}</td>
            <td className="px-2 py-1.5 text-right font-mono tabular-nums">{r.units}</td>
            <td className="px-2 py-1.5 text-right font-mono tabular-nums font-bold">{fmtMoney(r.revenue)}</td>
            <td className="px-2 py-1.5 text-right font-mono tabular-nums">{fmtMoney(r.avg_ticket)}</td>
            <td className="px-2 py-1.5 text-right font-mono tabular-nums text-destructive">{fmtMoney(r.discount)}</td>
            <td className="px-2 py-1.5 text-right font-mono tabular-nums text-[#00713a]">{fmtMoney(r.commission)}</td>
          </tr>))}</tbody>
      </table>
    </div>
  </ReportShell>;
}

export function DiscountAuditReport({ dateFrom, dateTo, modelCategory, branchId, branchTypeId }) {
  const [d, setD] = useState({ rows: [], total_discount: 0, count: 0 });
  const [includeAccounted, setIncludeAccounted] = useState(false);
  useEffect(() => { (async () => {
    try {
      const params = { date_from: dateFrom, date_to: dateTo };
      if (modelCategory) params.model_category = modelCategory;
      if (branchId) params.branch_id = branchId;
      if (branchTypeId) params.branch_type_id = branchTypeId;
      if (includeAccounted) params.include_accounted = true;
      const { data } = await api.get("/reports/discount-audit", { params });
      setD(data);
    } catch (e) { toast.error(formatApiError(e)); }
  })(); }, [dateFrom, dateTo, modelCategory, branchId, branchTypeId, includeAccounted]);
  return <ReportShell csvRows={d.rows} csvName="discount-audit.csv">
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 flex-1 min-w-[280px]">
        <Tile label={includeAccounted ? "Sales with Any Discount" : "Sales with Unaccounted Discount"} value={fmtNumber(d.count)}/>
        <Tile label={includeAccounted ? "Total ₹ Discounted" : "Total ₹ Unaccounted"} value={fmtMoney(d.total_discount)} accent="warn"/>
      </div>
      <label className="flex items-center gap-2 text-xs cursor-pointer select-none" data-testid="discount-audit-include-accounted">
        <input
          type="checkbox"
          checked={includeAccounted}
          onChange={(e) => setIncludeAccounted(e.target.checked)}
          className="accent-ink"
        />
        <span className="font-mono uppercase tracking-wider text-[10px] text-ink">
          Show all discounts (including accounted)
        </span>
      </label>
    </div>
    <div className="text-xs text-ink-muted">
      {includeAccounted ? (
        <>Every sale where any discount was given is listed.&nbsp;
          <span className="font-mono">Discount = Total Discount − Company Disc.</span> (dealership-borne; OEM-funded Company Disc. shown in its own column).
        </>
      ) : (
        <>Only <b>unaccounted</b> rebates are listed. Form-discount and package-level company-discount are treated as intentional and netted against on-road before audit:&nbsp;
          <span className="font-mono">Unaccounted = On-Road − Form Disc. − Company Disc. − Sold At</span>.
        </>
      )}
    </div>
    <div className="border-2 border-ink overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="bg-canvas-panel"><tr>
          <th className="px-2 py-2 font-mono text-[10px] uppercase text-left whitespace-nowrap">Date</th>
          <th className="px-2 py-2 font-mono text-[10px] uppercase text-left whitespace-nowrap">Branch</th>
          <th className="px-2 py-2 font-mono text-[10px] uppercase text-left whitespace-nowrap">VIN</th>
          <th className="px-2 py-2 font-mono text-[10px] uppercase text-left whitespace-nowrap">Customer</th>
          <th className="px-2 py-2 font-mono text-[10px] uppercase text-left whitespace-nowrap">Package</th>
          {["On-Road","Form Disc.","Company Disc.",includeAccounted ? "Discount" : "Unaccounted","Expected","Sold At","%"].map(h => (
            <th key={h} className="px-2 py-2 font-mono text-[10px] uppercase text-right whitespace-nowrap">{h}</th>
          ))}
          <th className="px-2 py-2 font-mono text-[10px] uppercase text-left whitespace-nowrap">Approval</th>
          <th className="px-2 py-2 font-mono text-[10px] uppercase text-left whitespace-nowrap">Verified By</th>
        </tr></thead>
        <tbody>{d.rows.length === 0 ? (
          <tr><td colSpan={14} className="px-2 py-10 text-center text-ink-muted text-sm">
            {includeAccounted ? "No discounts of any kind in this period." : "No unaccounted discounts in this period."}
          </td></tr>
        ) : d.rows.map(r => (
          <tr key={r.sale_id} className="border-t border-canvas-line">
            <td className="px-2 py-1.5 font-mono whitespace-nowrap">{r.date}</td>
            <td className="px-2 py-1.5 text-ink-muted whitespace-nowrap">{r.branch_name}</td>
            <td className="px-2 py-1.5 font-mono text-[10px] whitespace-nowrap">{r.vin || "—"}</td>
            <td className="px-2 py-1.5 whitespace-nowrap">{r.customer_name || "—"}</td>
            <td className="px-2 py-1.5 whitespace-nowrap text-ink-muted">{r.package_name || "—"}</td>
            <td className="px-2 py-1.5 text-right font-mono tabular-nums whitespace-nowrap">{fmtMoney(r.on_road_price)}</td>
            <td className="px-2 py-1.5 text-right font-mono tabular-nums whitespace-nowrap text-ink-muted">
              {r.form_discount > 0 ? fmtMoney(r.form_discount) : "—"}
            </td>
            <td className="px-2 py-1.5 text-right font-mono tabular-nums whitespace-nowrap text-ink-muted">
              {r.company_discount > 0 ? fmtMoney(r.company_discount) : "—"}
            </td>
            <td className="px-2 py-1.5 text-right font-mono tabular-nums text-destructive font-bold whitespace-nowrap">{r.discount > 0 ? fmtMoney(r.discount) : "—"}</td>
            <td className="px-2 py-1.5 text-right font-mono tabular-nums whitespace-nowrap text-ink-muted">{fmtMoney(r.expected_price)}</td>
            <td className="px-2 py-1.5 text-right font-mono tabular-nums whitespace-nowrap">{fmtMoney(r.sale_price)}</td>
            <td className="px-2 py-1.5 text-right font-mono tabular-nums whitespace-nowrap">{r.discount_pct}%</td>
            <td className="px-2 py-1.5 font-mono text-[10px] uppercase tracking-wider whitespace-nowrap">
              {r.approval_status === "approved"
                ? <span className="text-[#00713a]">APPROVED</span>
                : r.approval_status === "pending_approval"
                  ? <span className="text-[#A35700]">PENDING</span>
                  : <span className="text-ink-muted">{r.approval_status}</span>}
            </td>
            <td className="px-2 py-1.5 text-[10px] text-ink-muted whitespace-nowrap">{r.verified_by || "—"}</td>
          </tr>))}</tbody>
      </table>
    </div>
  </ReportShell>;
}
